import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CalendarPickerProps {
  value: string | null; // YYYY-MM-DD
  onChange: (date: string) => void;
  onClose: () => void;
  minDate?: string;
  maxDate?: string;
}

const DAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  value,
  onChange,
  onClose,
  minDate,
  maxDate,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const initial = value ? new Date(value + "T12:00:00") : new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay to avoid the click that opened the calendar
    const timeout = setTimeout(() => document.addEventListener("mousedown", handler), 50);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("mousedown", handler);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);

    // Monday = 0, Sunday = 6 (ISO week)
    let startWeekday = firstDay.getDay() - 1;
    if (startWeekday < 0) startWeekday = 6;

    const cells: { date: Date; ymd: string; inMonth: boolean }[] = [];

    // Fill previous month days
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth, -i);
      cells.push({ date: d, ymd: toYMD(d), inMonth: false });
    }

    // Current month days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const d = new Date(viewYear, viewMonth, day);
      cells.push({ date: d, ymd: toYMD(d), inMonth: true });
    }

    // Fill next month days to complete last row
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(viewYear, viewMonth + 1, i);
        cells.push({ date: d, ymd: toYMD(d), inMonth: false });
      }
    }

    return cells;
  }, [viewYear, viewMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isDisabled = (ymd: string) => {
    if (minDate && ymd < minDate) return true;
    if (maxDate && ymd > maxDate) return true;
    return false;
  };

  const todayYMD = toYMD(new Date());

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        zIndex: 1000,
        background: "#1a1a1a",
        border: "1px solid rgba(236,72,153,0.3)",
        borderRadius: "12px",
        padding: "16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        minWidth: "300px",
        userSelect: "none",
      }}
    >
      {/* Header: Month/Year + nav */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <button
          onClick={prevMonth}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#EC4899",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(236,72,153,0.2)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
        >
          <ChevronLeft size={16} />
        </button>
        <div
          style={{
            fontWeight: 700,
            fontSize: "0.9rem",
            color: "#fff",
            letterSpacing: "0.5px",
          }}
        >
          {MONTHS_ES[viewMonth]} {viewYear}
        </div>
        <button
          onClick={nextMonth}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#EC4899",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(236,72,153,0.2)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day names row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          marginBottom: "4px",
        }}
      >
        {DAYS_ES.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: "0.65rem",
              fontWeight: 700,
              color: "rgba(236,72,153,0.6)",
              padding: "4px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
        }}
      >
        {days.map((cell) => {
          const selected = value === cell.ymd;
          const today = cell.ymd === todayYMD;
          const disabled = !cell.inMonth || isDisabled(cell.ymd);

          return (
            <button
              key={cell.ymd + (cell.inMonth ? "" : "-out")}
              onClick={() => {
                if (!disabled) {
                  onChange(cell.ymd);
                  onClose();
                }
              }}
              disabled={disabled}
              style={{
                width: "100%",
                aspectRatio: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.8rem",
                fontWeight: selected ? 800 : today ? 700 : 500,
                color: disabled
                  ? "rgba(255,255,255,0.12)"
                  : selected
                    ? "#fff"
                    : today
                      ? "#EC4899"
                      : "rgba(255,255,255,0.7)",
                background: selected ? "#EC4899" : "transparent",
                border:
                  today && !selected ? "1px solid rgba(236,72,153,0.4)" : "1px solid transparent",
                borderRadius: "8px",
                cursor: disabled ? "default" : "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!disabled && !selected) {
                  e.currentTarget.style.background = "rgba(236,72,153,0.15)";
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !selected) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = today ? "#EC4899" : "rgba(255,255,255,0.7)";
                }
              }}
            >
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
